import { connectionQualityMeterLevel } from "./media/diagnostics.ts";

export type QualitySeverity = "info" | "good" | "warning" | "critical";
export type QualityKind = "host" | "viewer";
export type DetailSection = "connection" | "video" | "audio";
export type DetailValues = {
  values: Record<string, string | number | null> | null;
  emptyMessage: string;
};
export type Diagnostic = {
  code?: string;
  severity: string;
  title?: string;
  checks: { label: string; status: string; value: string }[];
};
export type QualityOverview = {
  code: string | null;
  severity: QualitySeverity;
  titleKey: string | null;
  title: string;
  level: number;
  checks: { labelKey: string | null; label: string; status: string; value: string }[];
};
export type ConnectionQualityDetails = {
  overview: QualityOverview | null;
  summary: string;
  connection: DetailValues;
  video: DetailValues;
  audio: DetailValues;
};

const diagnosticTitles: Record<string, string> = {
  cpu: "diagnostics.cpuTitle",
  bandwidth: "diagnostics.bandwidthTitle",
  network: "diagnostics.networkTitle",
  configured: "diagnostics.configuredTitle",
  browser: "diagnostics.browserTitle",
  framerate: "diagnostics.framerateTitle",
  gathering: "diagnostics.gatheringTitle",
  healthy: "diagnostics.healthyTitle",
  "publisher-gathering": "diagnostics.publisherGatheringTitle",
  "publisher-network": "diagnostics.publisherNetworkTitle",
  "publisher-healthy": "diagnostics.publisherHealthyTitle",
};
const checkLabels: Record<string, string> = {
  "Frame rate": "diagnostics.frameRate",
  Encoder: "diagnostics.encoder",
  "Upload capacity": "diagnostics.uploadCapacity",
  "Viewer path": "diagnostics.viewerPath",
  "Packet loss": "diagnostics.packetLossLabel",
  Codec: "diagnostics.codec",
  "Receive rate": "diagnostics.receiveRate",
  "Upload rate": "diagnostics.uploadRate",
  Latency: "diagnostics.latency",
};

// Preserve the shared diagnostics engine's measurements, publishing UI data
// only. React translates keys at render time, including after language changes.
export function connectionQualityOverview(diagnostic: Diagnostic): QualityOverview {
  const severity = normalizeQualitySeverity(diagnostic.severity);
  return {
    code: diagnostic.code ?? null,
    severity,
    titleKey:
      diagnosticTitles[diagnostic.code ?? ""] ??
      (diagnostic.title ? null : "connection.collectingMeasurements"),
    title: diagnostic.title ?? "",
    level: connectionQualityMeterLevel(severity),
    checks: diagnostic.checks.map((item) => ({
      ...item,
      labelKey: checkLabels[item.label] ?? null,
    })),
  };
}

export function normalizeQualitySeverity(severity: string): QualitySeverity {
  return severity === "good" || severity === "warning" || severity === "critical"
    ? severity
    : "info";
}

export function qualitySeverityLabelKey(severity: QualitySeverity): string {
  return severity === "good"
    ? "focused.healthy"
    : severity === "critical"
      ? "focused.actionNeeded"
      : severity === "warning"
        ? "focused.degraded"
        : "common.measuring";
}

export function diagnosticCheckValue(
  value: string,
  translate: (_key: string, _options?: { value: string }) => string,
): string {
  if (value === "Collecting") return translate("common.collecting");
  if (value === "Unavailable") return translate("common.unavailable");
  if (value === "CPU limited") return translate("diagnostics.cpuLimited");
  const available = value.match(/^(.*) available$/);
  if (available) return translate("diagnostics.bitrateAvailable", { value: available[1] });
  return value.replace(/([\d.]+)% loss/g, (_, amount: string) =>
    translate("diagnostics.packetLoss", { value: amount }),
  );
}

export function detailValues(
  values: DetailValues["values"] | undefined,
  emptyMessage: string,
): DetailValues {
  return { values: values ?? null, emptyMessage };
}
